// src/pages/session/LearnSessionPage.js

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Joyride from 'react-joyride';
import { useAuth } from '../../firebase/useAuth';
import { useMetadata } from '../../contexts/MetadataProvider';
import { useModal } from '../../context/ModalContext';
import { useToast } from '../../context/ToastContext';
import { fetchLearnSessionItems } from '../../firebase/fetchLearnSessionItems';
import { addSentenceToLearned } from '../../firebase/learnSession';
import { skipSentence } from '../../firebase/sentences';
import { getSound } from '../../firebase/sounds';
import { markSessionTourAsFinished } from '../../firebase/user';
import levelUpSoundFile from '../../assets/sounds/correct.mp3';

// Components
import DetailsModal from '../../components/modals/DetailsModal/DetailsModal';
import ReportIssueModal from '../../components/modals/ReportIssueModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import SessionLayout from '../../components/session/SessionLayout';
import InputSection from '../../components/session/InputSection';
import InteractiveCard from '../../components/session/InteractiveCard';
import Spinner from '../../components/common/Spinner';
import SimpleAnswerSection from '../../components/session/SimpleAnswerSection';
import { AudioPlayerProvider } from '../../context/AudioPlayerContext';
import { checkSentenceAnswer } from '../../utils/answerCheck';
import { willEarnShieldToday } from '../../utils/streak';


const LearnSessionPage = () => {
  const { userId, userData, userLoading, emailVerified, subscriptionStatus } = useAuth();
  const { learnedWordIds, wordIdsLoading, learnedTodayData, learnedSentenceIds, reviewedWordsData, reviewedSentencesData, loading: metadataLoading } = useMetadata();
  const { showModal, hideModal, modalStack } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const isExtraStudy = !!location.state?.isExtraStudy;
  const { showToast } = useToast();

  // State
  const [learnedSentences, setLearnedSentences] = useState([]);
  const [unlearnedSentences, setUnlearnedSentences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [checkSubmitted, setCheckSubmitted] = useState(false);
  const [inputResults, setInputResults] = useState([]);
  const [lastLearnedSentenceIndex, setLastLearnedSentenceIndex] = useState(null);
  const [learnedCount, setLearnedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [levelUpAlertState, setLevelUpAlertState] = useState({ show: false, level: '' });
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFetchKey, setAudioFetchKey] = useState(0);
  const [isCorrect, setIsCorrect] = useState(null);
  const [userChoice, setUserChoice] = useState(null);
  const [runTour, setRunTour] = useState(false);
  const [tourPaused, setTourPaused] = useState(false);
  const [sessionSettings, setSessionSettings] = useState(() => {
    return (
      JSON.parse(localStorage.getItem('sessionSettings')) || {
        playCorrectSound: true,
        playWrongSound: true,
        showTranslation: true,
        inputMode: 'mixed',
        simpleAnswerMode: false,
        showFlippingAnimation: true,
        reducedAnimations: false,
        showKaraokeMode: true,
        autoPlayAfterAnswer: false,
        stayOnQuestionUntilCorrect: false,
      }
    );
  });
  const [pendingRetry, setPendingRetry] = useState(false);

  // Refs and Memos
  const inputRef = useRef(null);
  const initialFetchDone = useRef(false);
  const shieldEarnedRef = useRef(null); // whether this session will earn a 盾 (decided at start)
  const levelUpSound = useMemo(() => new Audio(levelUpSoundFile), []);
  const currentSentence = unlearnedSentences[currentSentenceIndex];

  const isFullyLoading = loading || wordIdsLoading;

  // --- Side Effects (useEffect Hooks) ---
  useEffect(() => {
    if (userLoading) return;

    if (userId && !emailVerified) {
      navigate('/dashboard', {
        state: {
          errorMessage: 'Please verify your email to start a session.',
        },
      });
    }
  }, [userLoading, userId, emailVerified, navigate]);

  useEffect(() => {
    if (location.state?.errorMessage) {
      showToast(location.state.errorMessage, 'error');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showToast, navigate, location.pathname]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      const message = 'You have unsaved changes. Are you sure you want to leave?';
      event.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => {
      if (e.touches?.[0]?.touchType === "stylus") return;
      let el = e.target;
      while (el && el !== document.body) {
        const { overflowY, overflowX } = window.getComputedStyle(el);
        if (
          ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) ||
          ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth)
        ) return;
        el = el.parentElement;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    return () => document.removeEventListener('touchmove', preventTouchMove);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchAudio = async () => {
      if (currentSentence?.sound) {
        try {
          const url = await getSound(currentSentence.sound, 'sentences');
          if (isMounted) {
            setAudioUrl(url);
          }
        } catch (error) {
          console.error('Error fetching audio URL:', error);
        }
      } else if (!currentSentence) {
        setAudioUrl(null);
      }
    };
    fetchAudio();
    return () => {
      isMounted = false;
    };
  }, [currentSentence, audioFetchKey]);

  useEffect(() => {
    // Decide at session start (before this session's activity) whether finishing
    // today will earn a new 盾, so the end screen can just read the result.
    if (shieldEarnedRef.current === null && !metadataLoading) {
      shieldEarnedRef.current = willEarnShieldToday({
        learnedTodayData,
        reviewedWordsData,
        reviewedSentencesData,
        userTimezone: userData?.settings?.timezone || 'UTC',
        vacationStartedAt: userData?.settings?.vacation_started_at,
        vacationPeriods: userData?.vacation_history,
      });
    }

    const fetchSentences = async () => {
      if (!userId || !userData || initialFetchDone.current) return;
      // Claim the fetch before awaiting. This effect re-runs on every metadata
      // snapshot, and a snapshot landing mid-fetch would otherwise slip past
      // the guard and start a second full session load.
      initialFetchDone.current = true;
      setLoading(true);
      try {
        const extraCount = isExtraStudy ? 5 : 0;
        const sentencesData = await fetchLearnSessionItems(userId, userData, learnedTodayData, learnedSentenceIds, subscriptionStatus.isSubscribed, extraCount);
        if (sentencesData.length === 0) {
          navigate('/dashboard', { state: { errorMessage: 'No items to learn right now.' } });
          return;
        }
        setUnlearnedSentences(sentencesData);
        setTotalCount(sentencesData.length);
      } catch (error) {
        initialFetchDone.current = false; // release the claim so a later snapshot can retry
        console.error('Error fetching sentences:', error);
        showToast('Failed to load session.', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading && userId && userData && !subscriptionStatus.loading) {
      fetchSentences();
    }
  }, [
    userId,
    userData,
    learnedTodayData,
    learnedSentenceIds,
    reviewedWordsData,
    reviewedSentencesData,
    metadataLoading,
    navigate,
    userLoading,
    showToast,
    subscriptionStatus.loading,
    subscriptionStatus.isSubscribed,
    isExtraStudy,
  ]);

  useEffect(() => {
    if (!isFullyLoading && currentSentence && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFullyLoading, currentSentence, checkSubmitted]); // Use isFullyLoading

  // Tour effect
  useEffect(() => {
    if (userData && !isFullyLoading && currentSentence) {
      if (!userData.finished_session_tour) {
        setRunTour(true);
      }
    }
  }, [userData, isFullyLoading, currentSentence]);

  // Resume tour when back face appears after being paused
  useEffect(() => {
    if (tourPaused && checkSubmitted && !runTour) {
      // Increased delay to ensure the back face, spacers, and overflow are fully settled
      const timer = setTimeout(() => {
        setRunTour(true);
        // Dispatch multiple resize events to flush internal bounding boxes
        // The first one handles immediate re-renders, the second handles trailing animations
        setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
        setTimeout(() => window.dispatchEvent(new Event('resize')), 500);
      }, 1200); // Increased from 800ms to allow for flip + spacer shifts
      return () => clearTimeout(timer);
    }
  }, [tourPaused, checkSubmitted, runTour]);

  // Tour steps - split into initial and back face steps
  const initialTourSteps = [
    {
      target: '.session-tour-target-wrapper',
      content: 'This is the main sentence card. Listen to the audio and type what you hear!',
      disableBeacon: true,
    },
    {
      target: '.session-input-area',
      content: 'Type your answer here. The system will check your input after submission.',
    },
    {
      target: '.session-check-button',
      content: "Click here to check your answer when you're ready.",
    },
    {
      target: '.session-settings',
      content: 'Customize your session here! Switch between Kanji and Kana input, change the audio hotkey from Space to Backtick (`) for easier typing in Japanese, and more.',
    },
    {
      target: 'body',
      content: "Great! Now submit an answer to see what happens next... 🦊",
      placement: 'center',
    },
  ];

  const backFaceTourSteps = [
    {
      target: '.session-tour-target-wrapper',
      content: 'Here\'s the back of the card! It shows the sentence with pronunciation and translation.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="clickable-words"]',
      content: 'Each word in the sentence is clickable! Click any word to see its details and add it to your vocabulary.',
    },
    {
      target: '[data-tour="show-translation-button"]',
      content: 'Click this button or press "T" to reveal the translation when it\'s hidden.',
    },
    {
      target: '[data-tour="card-actions"]',
      content: 'Use these action buttons to report issues or skip sentences you already know.',
    },
    {
      target: 'body',
      content: "Perfect! You're all set to start learning! 🦊🎧",
      placement: 'center',
    },
  ];

  const tourSteps = tourPaused ? backFaceTourSteps : initialTourSteps;

  const handleTourCallback = (data) => {
    const { status } = data;

    if (status === 'skipped') {
      markSessionTourAsFinished(userId);
      setRunTour(false);
      setTourPaused(false);
    } else if (status === 'finished') {
      if (!tourPaused) {
        // First tour phase completed, pause and wait for back face
        setTourPaused(true);
        setRunTour(false);
      } else {
        // Second tour phase completed, finish tour
        markSessionTourAsFinished(userId);
        setRunTour(false);
        setTourPaused(false);
      }
    }

  };

  // --- Callbacks & Handlers ---

  const handlePlayEnd = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleWordClick = useCallback(
    (word) => {
      showModal(<DetailsModal item={{ ...word }} handleClose={hideModal} />);
    },
    [showModal, hideModal]
  );

  const handleReportClick = useCallback(
    (currentUserInput) => {
      showModal(<ReportIssueModal item={currentSentence} onClose={hideModal} userInput={currentUserInput} />);
    },
    [showModal, hideModal, currentSentence]
  );

  const handleCulturalContextClick = useCallback(() => {
    if (currentSentence) {
      showModal(<DetailsModal item={currentSentence} handleClose={hideModal} initialTab="context" />);
    }
  }, [showModal, hideModal, currentSentence]);

  const handlePolitenessClick = useCallback(() => {
    if (currentSentence) {
      showModal(<DetailsModal item={currentSentence} handleClose={hideModal} initialTab="politeness" />);
    }
  }, [showModal, hideModal, currentSentence]);

  const handleSkipConfirm = useCallback(() => {
    if (!currentSentence) return;
    skipSentence(userId, currentSentence.id, subscriptionStatus.isSubscribed)
      .then((result) => {
        if (result?.status === 'already_skipped') {
          showToast('This sentence is already skipped.', 'info');
        }
      })
      .catch((error) => {
        showToast(`Error skipping sentence: ${error.message}`, 'error');
      });

    const itemToProcess = { ...currentSentence, skipped: true };

    if (sessionSettings?.playCorrectSound) {
      levelUpSound.play();
    }

    setLearnedSentences((prev) => [...prev, itemToProcess]);
    setLearnedCount((prev) => prev + 1);

    setUnlearnedSentences((prev) => {
      const updatedSentences = prev.filter((_, index) => index !== currentSentenceIndex);
      if (updatedSentences.length > 0) {
        setCurrentSentenceIndex(Math.floor(Math.random() * updatedSentences.length));
      } else {
        const finalProcessedSentences = [...learnedSentences, itemToProcess];
        const sanitizedSentences = finalProcessedSentences.map((s) => {
          const sanitized = { ...s };
          sanitized.learned = true;
          // Stage 7 is the display convention for a skipped item. Keep the
          // `skipped` flag so the end-screen card still shows its badge, the
          // same way the review sessions pass it through.
          sanitized.srs_stage = sanitized.skipped ? 7 : 1;
          delete sanitized.sentence_ref;
          if (sanitized.words) {
            sanitized.words = sanitized.words.map((w) => {
              const cleanWord = { ...w };
              delete cleanWord.word_ref;
              delete cleanWord.word_id;
              return cleanWord;
            });
          }
          return sanitized;
        });
        navigate('/session/end', { state: { correctSentences: sanitizedSentences, type: 'learn_sentences', shieldEarned: shieldEarnedRef.current } });
      }
      return updatedSentences;
    });

    setUserInput('');
    setInputResults([]);
    setCheckSubmitted(false);
    setIsCorrect(null);
    setUserChoice(null);
    inputRef.current?.focus();
  }, [
    currentSentence,
    userId,
    subscriptionStatus.isSubscribed,
    sessionSettings?.playCorrectSound,
    showToast,
    levelUpSound,
    currentSentenceIndex,
    learnedSentences,
    navigate,
  ]);

  const handleSkipClick = useCallback(() => {
    showModal(
      <ConfirmationModal
        type="skip"
        onClose={hideModal}
        onConfirm={() => {
          handleSkipConfirm();
          hideModal();
        }}
      />
    );
  }, [showModal, hideModal, handleSkipConfirm]);

  const handleSettingsUpdate = (newSettings) => {
    if (newSettings.simpleAnswerMode !== sessionSettings.simpleAnswerMode && userChoice === null) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
    }
    setSessionSettings(newSettings);
  };

  const handleLearnedSentence = useCallback(() => {
    if (!currentSentence) return;
    setUserChoice(true);
    setLastLearnedSentenceIndex(currentSentenceIndex);
    addSentenceToLearned(userId, currentSentence.id, subscriptionStatus.isSubscribed, isExtraStudy, currentSentence.sound_duration)
      .then((result) => {
        if (result?.status === 'already_learned') {
          showToast('This sentence is already in your learning list.', 'info');
        }
      })
      .catch((error) => {
        showToast(`Error learning sentence: ${error.message}`, 'error');
      });
    setLearnedSentences((prev) => [...prev, currentSentence]);
    setLearnedCount((prev) => prev + 1);
    if (sessionSettings?.playCorrectSound) {
      levelUpSound.play();
    }
    setLevelUpAlertState({ show: true, level: 1, prevLevel: 0, type: 'up' });
  }, [currentSentence, currentSentenceIndex, levelUpSound, sessionSettings?.playCorrectSound, showToast, userId, subscriptionStatus.isSubscribed, isExtraStudy]);

  const handleCheck = useCallback(() => {
    if (!currentSentence) return;
    const { inputResults: newInputResults, isCorrect: isInputCorrect } = checkSentenceAnswer(
      currentSentence,
      userInput,
      sessionSettings?.inputMode || 'mixed'
    );

    setInputResults(newInputResults);
    setCheckSubmitted(true);
    setIsCorrect(isInputCorrect);
    if (isInputCorrect) {
      handleLearnedSentence();
    } else {
      setUserChoice(false);
      setPendingRetry(true);
    }
  }, [currentSentence, sessionSettings, userInput, handleLearnedSentence]);

  const handleNextSentence = useCallback(() => {
    const shouldStay = pendingRetry && sessionSettings?.stayOnQuestionUntilCorrect;
    setPendingRetry(false);
    if (shouldStay) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
      setUserChoice(null);
      setAudioUrl(null);
      setAudioFetchKey((prev) => prev + 1);
      inputRef.current?.focus();
      return;
    }
    setLevelUpAlertState({ show: false, level: '' });
    setUserChoice(null);
    setAudioUrl(null);
    setAudioFetchKey((prev) => prev + 1);

    if (lastLearnedSentenceIndex !== null) {
      setUnlearnedSentences((prev) => {
        const updatedSentences = prev.filter((_, index) => index !== lastLearnedSentenceIndex);
        if (updatedSentences.length > 0) {
          setCurrentSentenceIndex(Math.floor(Math.random() * updatedSentences.length));
        } else {
          const finalProcessedSentences = [...learnedSentences];
          const sanitizedSentences = finalProcessedSentences.map((s) => {
            const sanitized = { ...s };
            sanitized.learned = true;
            // Stage 7 is the display convention for a skipped item. Keep the
            // `skipped` flag so the end-screen card still shows its badge, the
            // same way the review sessions pass it through.
            sanitized.srs_stage = sanitized.skipped ? 7 : 1;
            delete sanitized.sentence_ref;
            if (sanitized.words) {
              sanitized.words = sanitized.words.map((w) => {
                const cleanWord = { ...w };
                delete cleanWord.word_ref;
                delete cleanWord.word_id;
                return cleanWord;
              });
            }
            return sanitized;
          });
          navigate('/session/end', { state: { correctSentences: sanitizedSentences, type: 'learn_sentences', shieldEarned: shieldEarnedRef.current } });
        }
        return updatedSentences;
      });
      setLastLearnedSentenceIndex(null);
    } else {
      let nextIndex = currentSentenceIndex;
      if (unlearnedSentences.length > 1) {
        const randomIndex = Math.floor(Math.random() * (unlearnedSentences.length - 1));
        nextIndex = randomIndex >= currentSentenceIndex ? randomIndex + 1 : randomIndex;
      }
      setCurrentSentenceIndex(nextIndex);
    }
    setUserInput('');
    setInputResults([]);
    setCheckSubmitted(false);
    setIsCorrect(null);
    inputRef.current?.focus();
  }, [currentSentenceIndex, lastLearnedSentenceIndex, learnedSentences, navigate, unlearnedSentences.length, pendingRetry, sessionSettings?.stayOnQuestionUntilCorrect]);

  const handleSimpleModeChoice = (isCorrectChoice) => {
    if (userChoice !== null) return;
    setUserChoice(isCorrectChoice);
    setIsCorrect(isCorrectChoice);
    setInputResults(
      currentSentence.words.map((w) => ({
        correct: isCorrectChoice,
        text: w.sentence_form,
      }))
    );
    if (isCorrectChoice) {
      handleLearnedSentence();
    } else {
      setPendingRetry(true);
    }
    setCheckSubmitted(true);
  };

  const isAnyModalOpen = modalStack.length > 0;

  const [isPenMode, setIsPenMode] = useState(false);
  useEffect(() => {
    const handlePointerDown = (e) => {
      if (e.pointerType === 'pen') {
        setIsPenMode(true);
      }
    };
    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  // Use isFullyLoading instead of just loading
  if ((isFullyLoading && unlearnedSentences.length === 0) || !currentSentence)
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <Spinner />
      </div>
    );

  return (
    <>
      {!isFullyLoading && runTour && (
        <Joyride
          key={tourPaused ? 'back-phase' : 'initial-phase'}
          steps={tourSteps}
          run={runTour}
          continuous
          showSkipButton
          disableScrollParentFix
          disableScrolling
          scrollToFirstStep={false}
          floaterProps={{ disableAnimation: true }}
          scrollOffset={220}
          locale={{
            back: 'Go Back',
            last: 'Start Learning!',
            next: 'Next',
            skip: 'Skip Tour',
          }}
          callback={handleTourCallback}
          styles={{
            options: { primaryColor: 'var(--color-primary)', zIndex: 1000 },
            tooltip: {
              maxWidth: 'calc(100vw - 32px)',
              width: '100%',
            },
          }}
        />
      )}
      <div className="h-dvh overflow-hidden">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 z-40">
            <SessionLayout correctCount={learnedCount} totalCount={totalCount} updateSettings={handleSettingsUpdate} />
          </div>

          {/* Card area: fills remaining space */}
          <div className={`${!sessionSettings.simpleAnswerMode && isPenMode ? 'flex-shrink flex-grow-0' : 'flex-1'} min-h-0 flex flex-col w-full max-w-4xl mx-auto`}>
            <AudioPlayerProvider
              audioSrc={audioUrl}
              onPlayEnd={handlePlayEnd}
              isAutoplay={!checkSubmitted}
              keepAwake={sessionSettings.keepAudioDeviceAwake}
            >
              <InteractiveCard
                currentItem={currentSentence}
                isSubmitted={checkSubmitted}
                showFlippingAnimation={sessionSettings.showFlippingAnimation}
                isIgnored={false}
                isGraded={userChoice !== null}
                showTranslation={checkSubmitted && sessionSettings.showTranslation}
                answerResult={userChoice === true ? 'correct' : userChoice === false ? 'wrong' : null}
                onItemClick={handleWordClick}
                isHotkeysEnabled={!isAnyModalOpen}
                onIgnore={null}
                onSkip={handleSkipClick}
                onReport={handleReportClick}
                onCulturalContext={handleCulturalContextClick}
                onPoliteness={handlePolitenessClick}
                levelUpAlertState={levelUpAlertState}
                learnedWordIds={learnedWordIds}
                userInput={userInput}
                sessionSettings={sessionSettings}
                isTourRunning={runTour}
                shrinkToContent={!sessionSettings.simpleAnswerMode && isPenMode}
              />
            </AudioPlayerProvider>
          </div>

          {/* Input section */}
          <div className="flex-shrink-0 w-full bg-background-secondary border-t border-border z-50">
            <div className="w-full max-w-4xl mx-auto p-4 session-input-area">
              {sessionSettings.simpleAnswerMode ? (
                <SimpleAnswerSection
                  showResultCard={checkSubmitted}
                  onReveal={() => {
                    setCheckSubmitted(true);
                  }}
                  onChoice={handleSimpleModeChoice}
                  userChoice={userChoice}
                  isIgnored={false}
                  onNext={handleNextSentence}
                />
              ) : (
                <InputSection
                  ref={inputRef}
                  checkSubmitted={checkSubmitted}
                  userInput={userInput}
                  inputResults={inputResults}
                  setUserInput={setUserInput}
                  handleCheck={handleCheck}
                  handleNext={handleNextSentence}
                  isCorrect={isCorrect}
                  isIgnored={false}
                  isAnyModalOpen={isAnyModalOpen}
                  inputMode={sessionSettings?.inputMode || 'mixed'}
                  autoConvertToKana={sessionSettings?.autoConvertToKana ?? true}
                  reducedAnimations={sessionSettings?.reducedAnimations}
                />
              )}
            </div>
          </div>

          {/* Palm rest spacer: only in pen mode (input mode only) */}
          {!sessionSettings.simpleAnswerMode && isPenMode && <div className="flex-1 min-h-0 pointer-events-none" />}
        </div>
      </div>
    </>
  );
};

export default LearnSessionPage;
